import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskLine } from "./Reveal";
import { whatsappLink } from "../../lib/api";
import { ArrowDown } from "lucide-react";

const HERO_IMG =
  "https://customer-assets-gfyr7b9c.emergentagent.net/job_toga-menswear/artifacts/7d026a524ed3f9a4_1793001027132.webp";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen bg-cream overflow-hidden">
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 pt-32 md:pt-40 pb-16 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center min-h-screen">
        {/* Text */}
        <motion.div style={{ y: textY }} className="lg:col-span-7 relative z-10">
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 0.8 }}
            className="text-xs tracking-[0.3em] uppercase font-bold text-toga mb-8"
          >
            Vashi, Navi Mumbai — Menswear Store
          </motion.p>
          <h1 className="font-display font-black uppercase tracking-normal leading-[0.9] text-charcoal text-[clamp(2.75rem,7.5vw,8.5rem)]">
            <MaskLine delay={0.25}>Readymade</MaskLine>
            <MaskLine delay={0.4} className="text-toga">& Custom</MaskLine>
            <MaskLine delay={0.55}>Made.</MaskLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.9 }}
            className="mt-8 max-w-md text-lg font-light text-charcoal/70 leading-relaxed"
          >
            Fine fabrics, hand-finished kurtas and custom tailoring — made for the modern man.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.9 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <a href="#kurtas" data-testid="hero-explore-cta" className="bg-charcoal text-cream px-9 py-4 text-xs uppercase tracking-[0.18em] font-bold hover:bg-toga transition-colors duration-300">Explore Collection</a>
            <a href={whatsappLink("Hello TOGA, I'd like to book an appointment.")} target="_blank" rel="noreferrer" data-testid="hero-book-cta" className="border border-charcoal text-charcoal px-9 py-4 text-xs uppercase tracking-[0.18em] font-bold hover:bg-charcoal hover:text-cream transition-colors duration-300">Book Appointment</a>
          </motion.div>
        </motion.div>

        {/* Image — clipped spotlight frame */}
        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, clipPath: "inset(100% 0 0 0)" }}
            animate={{ opacity: 1, clipPath: "inset(0% 0 0 0)" }}
            transition={{ delay: 0.5, duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
            className="relative aspect-[3/4] overflow-hidden border border-charcoal/15"
          >
            <motion.img
              style={{ y: imgY, scale: imgScale }}
              src={HERO_IMG}
              alt="TOGA signature kurta"
              className="absolute inset-0 w-full h-[120%] object-cover"
            />
            <div className="absolute bottom-0 left-0 right-0 bg-charcoal/85 backdrop-blur-sm px-6 py-4 flex items-center justify-between">
              <span className="text-cream text-xs uppercase tracking-[0.2em] font-bold">The Signature Look</span>
              <span className="text-toga text-xs uppercase tracking-[0.2em] font-bold">Latest</span>
            </div>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3, duration: 1 }}
        className="absolute bottom-8 left-6 md:left-10 flex items-center gap-3 text-charcoal/60"
      >
        <ArrowDown size={16} className="animate-bounce" />
        <span className="text-xs uppercase tracking-[0.25em] font-semibold">Scroll to discover</span>
      </motion.div>
    </section>
  );
}
